import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, X, Plus, GripVertical } from 'lucide-react'
import { api } from '../lib/api'

type CtaType = 'events_list' | 'catering_form' | 'category' | 'product' | 'event' | 'external_url'

const CTA_LABELS: Record<CtaType, string> = {
  events_list:   'Events & Tastings list',
  catering_form: 'Catering form',
  category:      'Specific category',
  product:       'Specific product',
  event:         'Specific event',
  external_url:  'External URL',
}

const CTA_VALUE_HINTS: Record<CtaType, string> = {
  events_list:   '(no value needed)',
  catering_form: '(no value needed)',
  category:      'Category slug (e.g. "whisky")',
  product:       'Product ID',
  event:         'Event ID',
  external_url:  'https://…',
}

interface Experience {
  id: string
  name_en: string
  name_ar: string
  subtitle_en: string | null
  subtitle_ar: string | null
  description_en: string | null
  description_ar: string | null
  image_key: string | null
  cta_type: CtaType
  cta_value: string | null
  sort_order: number
  active: number
}

export default function Experiences() {
  const [editing, setEditing] = useState<Experience | null | 'new'>(null)

  const { data, isLoading } = useQuery<{ experiences: Experience[] }>({
    queryKey: ['admin-experiences'],
    queryFn: () => api.get('/experiences').then(r => r.data),
  })

  const experiences = data?.experiences ?? []

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Sparkles size={24} className="text-[#C41E3A]" />
          <h1 className="text-2xl font-bold text-white">Experiences</h1>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#a01830] flex items-center gap-2"
        >
          <Plus size={14} /> New Experience
        </button>
      </div>

      <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4 mb-4">
        <p className="text-xs text-gray-400 leading-relaxed">
          Experiences appear on the customer app's <span className="text-white font-semibold">Experiences</span> tab, ordered by <span className="text-white">sort_order</span>.
          Each card promotes a service or section (JR Winery Tour, Cask & Cork pairings, etc.) and deep-links to events, catering, a category, a product, or an external URL.
        </p>
      </div>

      {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
      {!isLoading && experiences.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-12">No experiences yet.</p>
      )}

      <div className="space-y-3">
        {experiences.map(e => (
          <div key={e.id} className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg overflow-hidden flex">
            {e.image_key && (
              <img src={`https://files.drinx.jo/${e.image_key}`} alt=""
                className="w-32 h-32 object-cover bg-[#0a0a0a] flex-shrink-0" />
            )}
            <div className="flex-1 p-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <GripVertical size={12} className="text-gray-600" />
                  <span className="text-[10px] text-gray-500">#{e.sort_order}</span>
                  {!e.active && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-500/15 text-gray-400 border border-gray-500/30">INACTIVE</span>}
                </div>
                <h3 className="text-[#C41E3A] font-bold text-base">{e.name_en}</h3>
                <p className="text-sm text-[#C41E3A]/80 font-arabic">{e.name_ar}</p>
                {e.subtitle_en && <p className="text-xs text-gray-400 mt-1">{e.subtitle_en}</p>}
                <p className="text-[10px] text-gray-500 mt-2">
                  → {CTA_LABELS[e.cta_type]}{e.cta_value ? ` · ${e.cta_value}` : ''}
                </p>
              </div>
              <button onClick={() => setEditing(e)}
                className="px-3 py-1.5 rounded text-xs bg-white/5 border border-white/10 text-white hover:bg-white/10">
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ExperienceFormModal
          experience={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function ExperienceFormModal({ experience, onClose }: { experience?: Experience; onClose: () => void }) {
  const qc = useQueryClient()
  const [nameEn, setNameEn] = useState(experience?.name_en ?? '')
  const [nameAr, setNameAr] = useState(experience?.name_ar ?? '')
  const [subtitleEn, setSubtitleEn] = useState(experience?.subtitle_en ?? '')
  const [subtitleAr, setSubtitleAr] = useState(experience?.subtitle_ar ?? '')
  const [descEn, setDescEn] = useState(experience?.description_en ?? '')
  const [descAr, setDescAr] = useState(experience?.description_ar ?? '')
  const [imageKey, setImageKey] = useState(experience?.image_key ?? '')
  const [ctaType, setCtaType] = useState<CtaType>(experience?.cta_type ?? 'events_list')
  const [ctaValue, setCtaValue] = useState(experience?.cta_value ?? '')
  const [sortOrder, setSortOrder] = useState(experience?.sort_order?.toString() ?? '0')
  const [active, setActive] = useState(experience?.active !== 0)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      if (experience) return api.patch(`/experiences/${experience.id}`, payload).then(r => r.data)
      return api.post('/experiences', payload).then(r => r.data)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-experiences'] })
      onClose()
    },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  })

  const remove = useMutation({
    mutationFn: () => api.delete(`/experiences/${experience!.id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-experiences'] })
      onClose()
    },
  })

  async function handleUpload(file: File) {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/experiences/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setImageKey(res.data.key)
    } catch {
      setError('Image upload failed')
    } finally {
      setUploading(false)
    }
  }

  const valueRequired = ctaType !== 'events_list' && ctaType !== 'catering_form'

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[#2e2e2e] flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">{experience ? 'Edit Experience' : 'New Experience'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name (English)" value={nameEn} onChange={setNameEn} />
            <Field label="Name (Arabic)" value={nameAr} onChange={setNameAr} dir="rtl" />
            <Field label="Subtitle (English)" value={subtitleEn} onChange={setSubtitleEn} />
            <Field label="Subtitle (Arabic)" value={subtitleAr} onChange={setSubtitleAr} dir="rtl" />
            <Field label="Description (English)" value={descEn} onChange={setDescEn} multiline />
            <Field label="Description (Arabic)" value={descAr} onChange={setDescAr} multiline dir="rtl" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Tap action</label>
              <select value={ctaType} onChange={e => setCtaType(e.target.value as CtaType)} className={inputCls}>
                {(Object.keys(CTA_LABELS) as CtaType[]).map(t => (
                  <option key={t} value={t} className="bg-[#1a1a1a]">{CTA_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>
                {valueRequired ? 'CTA value' : 'CTA value (unused)'}
              </label>
              <input
                type="text"
                value={ctaValue}
                onChange={e => setCtaValue(e.target.value)}
                placeholder={CTA_VALUE_HINTS[ctaType]}
                disabled={!valueRequired}
                className={`${inputCls} ${!valueRequired ? 'opacity-50' : ''}`}
              />
            </div>
            <Field label="Sort order" value={sortOrder} onChange={setSortOrder} type="number" />
            <div className="flex items-end gap-2">
              <input type="checkbox" id="exp-active" checked={active}
                onChange={e => setActive(e.target.checked)}
                className="w-4 h-4 accent-[#C41E3A]" />
              <label htmlFor="exp-active" className="text-sm text-white">Active</label>
            </div>
          </div>

          <div>
            <label className={labelCls}>Hero image</label>
            {imageKey && (
              <img src={`https://files.drinx.jo/${imageKey}`} alt=""
                className="w-full h-40 object-cover rounded-lg mb-2 bg-[#0a0a0a]" />
            )}
            <input type="file" accept="image/*"
              onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])}
              disabled={uploading}
              className="text-xs text-gray-400 file:mr-2 file:px-3 file:py-1 file:rounded file:bg-white/10 file:text-white file:border-0 file:text-xs"
            />
            {uploading && <p className="text-xs text-gray-500 mt-1">Uploading…</p>}
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => save.mutate({
                name_en: nameEn, name_ar: nameAr,
                subtitle_en: subtitleEn || null, subtitle_ar: subtitleAr || null,
                description_en: descEn || null, description_ar: descAr || null,
                image_key: imageKey || null,
                cta_type: ctaType,
                cta_value: valueRequired ? (ctaValue || null) : null,
                sort_order: parseInt(sortOrder || '0', 10),
                active,
              })}
              disabled={save.isPending}
              className="flex-1 px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : experience ? 'Save' : 'Create'}
            </button>
            {experience && (
              <button onClick={() => remove.mutate()}
                className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-red-400 text-sm hover:bg-white/10">
                Deactivate
              </button>
            )}
            <button onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 text-sm hover:bg-white/10">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#C41E3A]'
const labelCls = 'block text-xs text-gray-400 mb-1'

function Field({ label, value, onChange, type = 'text', multiline = false, dir }: {
  label: string; value: string; onChange: (v: string) => void
  type?: string; multiline?: boolean; dir?: 'rtl' | 'ltr'
}) {
  return (
    <div className={multiline ? 'col-span-2' : ''}>
      <label className={labelCls}>{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} dir={dir} rows={3} className={inputCls} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} dir={dir} className={inputCls} />
      )}
    </div>
  )
}
